// src/pages/Profile.tsx
import PageHeader from '../components/PageHeader';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { supabase } from '../utils/supabase';
import { User } from '@supabase/supabase-js';
import { IoTrashOutline, IoAddOutline, IoChevronDown, IoChevronUp, IoDocumentOutline } from 'react-icons/io5';
import { InformationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useOutletContext } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { useBannerAutoRead } from '../hooks/useBannerAutoRead';
import { LinkedContentPreview } from '../components/LinkedContentPreview';
import { useToast } from '../context/ToastContext';
import WorkExperienceDocumentUploadModal from '../components/WorkExperienceDocumentUploadModal';
import WorkExperienceDocumentPreview, { WorkExperienceDocument } from '../components/WorkExperienceDocumentPreview';
import { FileService } from '../utils/fileService';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { useAuth } from '../context/AuthContext';
import OnboardingBanner from '../components/OnboardingBanner';
import useUserTimezone from '../hooks/useUserTimezone';
import { useUserEmails } from '../hooks/useUserEmails';
import { EmailList } from '../components/Profile/EmailList';
import { AddEmailForm } from '../components/Profile/AddEmailForm';
import { EditEmailModal } from '../components/Profile/EditEmailModal';
import { EmailInfoModal } from '../components/Profile/EmailInfoModal';

const logger = createLogger({ module: 'Profile', level: 'info' });

interface WorkExperience {
  id?: string;
  company: string;
  position: string;
  location: string;
  start_date: string;
  end_date: string | null;
  description: string;
  is_promotion: boolean;
  is_current: boolean;
  previous_position_id: string | null;
  previous_position?: WorkExperience | null;
  compensation: CompensationEntry[];
}

interface CompensationDetails {
  shares?: number;
  vestingSchedule?: string;
  strikePrice?: number;
  additionalInfo?: string;
}

interface CompensationEntry {
  type: 'salary' | 'hourly' | 'bonus' | 'stock_options' | 'restricted_stock_units' | 'other';
  amount: number;
  currency: string;
  pay_frequency?: 'yearly' | 'monthly' | 'bi-weekly' | 'weekly' | 'hourly' | 'one_time';
  effective_date: string;
  end_date?: string | null;
  details?: CompensationDetails;
}

interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  location: string | null;
  professional_summary: string | null;
  skills: string[] | null;
  career_objectives: string | null;
  job_title: string | null;
  company: string | null;
  created_at: string;
  updated_at: string;
  onboarding_complete: boolean;
  user_type: string;
}

interface LinkedContent {
  id: string;
  type: "work_experience" | "review" | "note" | "career_card" | "work_experience_document";
  preview: {
    title: string;
    date: string;
    preview: string;
  };
}

interface ScrollToExperience {
  shouldScroll: boolean;
  scrollToExperienceId: string;
}

const Profile: React.FC = () => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { user, userProfile, isAdmin } = useAuth();
  
  // Statsig analytics hooks
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  
  // Auto-mark banner notifications as read when landing on profile page
  useBannerAutoRead();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<UserProfile | null>(null);
  const [editedWorkExperiences, setEditedWorkExperiences] = useState<WorkExperience[]>([]);
  const [newSkill, setNewSkill] = useState('');
  const { scrollToExperienceId } = useOutletContext<{ scrollToExperienceId?: ScrollToExperience }>() || {};
  const [expandedCompensation, setExpandedCompensation] = useState<{ [key: string]: boolean }>({});
  
  // Email management
  const { 
    emails, 
    isLoading: isLoadingEmails, 
    addEmail, 
    removeEmail, 
    updateEmail, 
    resendVerification 
  } = useUserEmails();
  const [editingEmail, setEditingEmail] = useState<typeof emails[0] | null>(null);
  const [showEmailInfoModal, setShowEmailInfoModal] = useState(false);
  const [expandedLinkedContent, setExpandedLinkedContent] = useState<{ [key: string]: boolean }>({});
  const [linkedContent, setLinkedContent] = useState<{ [key: string]: LinkedContent[] }>({});
  const [isLoadingLinkedContent, setIsLoadingLinkedContent] = useState<{ [key: string]: boolean }>({});
  
  // New state for work experience documents
  const [workExperienceDocuments, setWorkExperienceDocuments] = useState<{ [key: string]: WorkExperienceDocument[] }>({});
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<{ [key: string]: boolean }>({});
  const [expandedDocuments, setExpandedDocuments] = useState<{ [key: string]: boolean }>({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedExperienceId, setSelectedExperienceId] = useState<string | null>(null);
  
  // Journey tracking
  const journeyIdRef = useRef<string>('');
  const journeyActiveRef = useRef<boolean>(false);
  const pageLoadStartTimeRef = useRef<number>(performance.now());

  // Add useUserTimezone hook for timezone-aware date formatting
  const { formatDateOnlyWithTimezone } = useUserTimezone();

  useEffect(() => {
    const scrollToExperience = async () => {
      if (scrollToExperienceId?.shouldScroll) {
        // Wait for the DOM to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const experienceElement = document.getElementById(`experience-${scrollToExperienceId.scrollToExperienceId}`);
        if (experienceElement) {
          experienceElement.scrollIntoView({ behavior: 'smooth' });
          // Highlight the element temporarily
          experienceElement.classList.add('highlight-experience');
          setTimeout(() => {
            experienceElement.classList.remove('highlight-experience');
          }, 2000);
        }
      }
    };

    scrollToExperience();
  }, [scrollToExperienceId]);

  useEffect(() => {
    if (user && userProfile) {
      fetchProfile();
      fetchWorkExperiences().then(async (experiences) => {
        if (experiences && experiences.length > 0) {
          // After fetching work experiences, prefetch linked content existence first
          const linkedContentChecks = experiences.map(exp => {
            if (exp.id) {
              return checkLinkedContentExists(exp.id);
            }
            return Promise.resolve();
          });
          
          // Wait for all linked content checks to complete
          await Promise.all(linkedContentChecks);
          
          // Then prefetch detailed data without affecting UI state
          experiences.forEach(exp => {
            if (exp.id) {
              fetchLinkedContent(exp.id);
              fetchWorkExperienceDocuments(exp.id);
            }
          });
        }
      });
    }
  }, [user, userProfile]);

  useEffect(() => {
    // Start profile management journey when component mounts
    const journeyId = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    journeyIdRef.current = journeyId;
    journeyActiveRef.current = true;
    
    // Track user journey start
    trackUserJourney('profile_management_started', {
      journey_id: journeyId,
      entry_point: 'direct',
      has_work_experiences: workExperiences.length > 0
    });
    
    // Track page view
    trackFeatureUsage('profile_page', 'viewed');
    
    // Track page load performance
    const loadTime = performance.now() - pageLoadStartTimeRef.current;
    trackFeatureUsage('page_performance', 'measured', {
      route: '/profile',
      load_time_ms: Math.round(loadTime),
      first_paint_ms: Math.round(
        performance.getEntriesByType('paint')
          .find(entry => entry.name === 'first-paint')?.startTime || loadTime
      )
    });
    
    return () => {
      // If user navigates away without completing, abandon journey
      if (journeyIdRef.current && journeyActiveRef.current) {
        trackUserJourney('profile_management_abandoned', {
          journey_id: journeyIdRef.current,
          reason: 'page_exit',
          time_spent_ms: performance.now() - pageLoadStartTimeRef.current
        });
        journeyActiveRef.current = false;
      }
    };
  }, [trackFeatureUsage, trackUserJourney, workExperiences.length]);

  const fetchProfile = async () => {
    const startTime = performance.now();
    try {
      if (!user || !userProfile) return;

      // Use the userProfile from AuthContext (already cached)
      const profileData: UserProfile = {
        id: userProfile.id,
        email: userProfile.email,
        first_name: userProfile.first_name,
        last_name: userProfile.last_name,
        location: userProfile.location,
        professional_summary: userProfile.professional_summary,
        skills: userProfile.skills || [],
        career_objectives: userProfile.career_objectives,
        job_title: userProfile.job_title,
        company: userProfile.company,
        created_at: userProfile.created_at,
        updated_at: userProfile.updated_at,
        onboarding_complete: userProfile.onboarding_complete,
        user_type: userProfile.user_type
      };
      
      // Track successful profile fetch (from cache)
      trackFeatureUsage('profile_fetch', 'completed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        from_cache: true,
        has_skills: profileData.skills && profileData.skills.length > 0,
        has_summary: !!profileData.professional_summary,
        has_objectives: !!profileData.career_objectives
      });

      setProfile(profileData);
      setEditedProfile(profileData);
    } catch (error) {
      logger.error('Error in fetchProfile:', error);
      
      // Track error
      trackFeatureUsage('profile_fetch', 'failed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const fetchWorkExperiences = async () => {
    if (!user) return [];

    const { data, error } = await supabase
      .from('work_experience')
      .select('*')
      .eq('user_id', user.id)
      .order('start_date', { ascending: false });

    if (error) {
      logger.error('Error fetching work experiences:', error);
      return [];
    }

    // Create a map of id to work experience
    const experienceMap = new Map(data.map(exp => [exp.id, exp]));

    // Link previous positions
    const linkedExperiences = data.map(exp => {
      if (exp.is_promotion && exp.previous_position_id) {
        return {
          ...exp,
          previous_position: experienceMap.get(exp.previous_position_id) || null
        };
      }
      return exp;
    });

    setWorkExperiences(linkedExperiences);
    setEditedWorkExperiences(linkedExperiences);

    return linkedExperiences;
  };

  // Add this function to quickly check if linked content exists
  const checkLinkedContentExists = async (experienceId: string) => {
    if (linkedContent[experienceId] !== undefined) {
      return; // Already fetched
    }
    
    try {
      // Just check if any linked content exists without loading details
      const { data: links, error } = await supabase
        .from('content_links')
        .select('source_id')
        .eq('target_id', experienceId)
        .eq('target_type', 'work_experience');

      if (error) throw error;

      // If links exist, set a placeholder value to mark that content exists
      setLinkedContent(prev => ({
        ...prev,
        [experienceId]: links && links.length > 0 ? [{} as any] : []
      }));
    } catch (error) {
      console.error('Error checking linked content existence:', error);
    }
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setEditedProfile(profile);
    setEditedWorkExperiences([...workExperiences]);
    
    // Track edit mode activation
    trackFeatureUsage('profile_edit_mode', 'activated', {
      journey_id: journeyIdRef.current,
      has_work_experiences: workExperiences.length > 0,
      profile_completeness: profile ? {
        has_skills: profile.skills && profile.skills.length > 0,
        has_summary: !!profile.professional_summary,
        has_objectives: !!profile.career_objectives
      } : {}
    });
  };

  const handleSaveProfile = async () => {
    const startTime = performance.now();
    try {
      if (!user || !editedProfile) return;

      // Update profile in the users table
      const { error: profileError } = await supabase
        .from('users')
        .update({
          first_name: editedProfile.first_name,
          last_name: editedProfile.last_name,
          location: editedProfile.location,
          professional_summary: editedProfile.professional_summary,
          skills: editedProfile.skills,
          career_objectives: editedProfile.career_objectives,
          job_title: editedProfile.job_title,
          company: editedProfile.company,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) {
        logger.error('Error updating profile:', profileError);
        showToast('Failed to update profile', 'error');
        
        // Track error in telemetry
        trackFeatureUsage('profile_save', 'failed', {
          journey_id: journeyIdRef.current,
          error_type: 'profile_save_error',
          error_message: profileError.message || 'Unknown error',
          duration_ms: Math.round(performance.now() - startTime)
        });
        
        trackUserJourney('profile_management_failed', {
          journey_id: journeyIdRef.current,
          failure_reason: 'profile_save_error',
          error_message: profileError.message || 'Unknown error',
          completion_time_ms: performance.now() - pageLoadStartTimeRef.current
        });
        journeyActiveRef.current = false;
        return;
      }

      // Update work experiences
      for (const experience of editedWorkExperiences) {
        const { id, previous_position, ...experienceData } = experience;
        
        // Skip any additional fields that are not in the database schema
        const workExperienceToUpdate = {
          company: experienceData.company,
          position: experienceData.position,
          location: experienceData.location,
          start_date: experienceData.start_date,
          end_date: experienceData.end_date,
          description: experienceData.description,
          is_promotion: experienceData.is_promotion,
          is_current: experienceData.is_current,
          previous_position_id: experienceData.previous_position_id,
          compensation: experienceData.compensation || null,
        };
        
        if (id) {
          // Update existing experience
          const { error } = await supabase
            .from('work_experience')
            .update(workExperienceToUpdate)
            .eq('id', id);

          if (error) {
            logger.error('Error updating work experience:', error);
            showToast('Failed to update work experience', 'error');
            
            // Track failed work experience update
            trackFeatureUsage('work_experience_update', 'failed', {
              journey_id: journeyIdRef.current,
              duration_ms: Math.round(performance.now() - startTime),
              error_message: error.message || 'Unknown error',
              experience_id: id
            });
            return;
          }
        } else {
          // Add new experience
          const { error } = await supabase
            .from('work_experience')
            .insert({
              ...workExperienceToUpdate,
              user_id: user.id,
            });

          if (error) {
            logger.error('Error adding work experience:', error);
            showToast('Failed to add work experience', 'error');
            
            // Track failed work experience creation
            trackFeatureUsage('work_experience_create', 'failed', {
              journey_id: journeyIdRef.current,
              duration_ms: Math.round(performance.now() - startTime),
              error_message: error.message || 'Unknown error',
              company: experienceData.company,
              position: experienceData.position
            });
            return;
          }
        }
      }

      setProfile(editedProfile);
      setWorkExperiences(editedWorkExperiences);
      setIsEditing(false);
      
      // Track successful profile save
      trackFeatureUsage('profile_save', 'completed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        work_experiences_count: editedWorkExperiences.length,
        skills_count: editedProfile.skills ? editedProfile.skills.length : 0,
        has_summary: !!editedProfile.professional_summary,
        has_objectives: !!editedProfile.career_objectives
      });
      
      // Track successful journey completion
      trackUserJourney('profile_management_completed', {
        journey_id: journeyIdRef.current,
        success: true,
        completion_time_ms: performance.now() - pageLoadStartTimeRef.current,
        changes_made: {
          profile_updated: true,
          work_experiences_count: editedWorkExperiences.length
        }
      });
      journeyActiveRef.current = false;
      
      showToast('Profile updated successfully', 'success');
      fetchWorkExperiences(); // Refetch to get any new IDs
    } catch (error) {
      logger.error('Error in handleSaveProfile:', error);
      
      // Track error
      trackFeatureUsage('profile_save', 'failed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
      
      trackUserJourney('profile_management_failed', {
        journey_id: journeyIdRef.current,
        failure_reason: 'unexpected_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        completion_time_ms: performance.now() - pageLoadStartTimeRef.current
      });
      journeyActiveRef.current = false;
    }
  };

  const handleCancelEdit = () => {
    setEditedProfile(profile);
    setEditedWorkExperiences(workExperiences);
    setIsEditing(false);
  };

  // Add type guard for UserProfile fields
  const isUserProfileField = (field: string): field is keyof UserProfile => {
    return ['first_name', 'last_name', 'location', 'professional_summary', 'career_objectives'].includes(field);
  };

  // Update the handleChange function to be type-safe
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const field = e.target.name;
    if (editedProfile && isUserProfileField(field)) {
      setEditedProfile({
        ...editedProfile,
        [field]: e.target.value,
      });
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && editedProfile && !editedProfile.skills.includes(newSkill.trim())) {
      setEditedProfile({
        ...editedProfile,
        skills: [...editedProfile.skills, newSkill.trim()],
      });
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    if (editedProfile) {
      setEditedProfile({
        ...editedProfile,
        skills: editedProfile.skills.filter(skill => skill !== skillToRemove),
      });
    }
  };

  const handleSkillInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handleAddWorkExperience = () => {
    const newWorkExperience: WorkExperience = {
      company: '',
      position: '',
      location: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: null,
      description: '',
      is_promotion: false,
      is_current: true,
      previous_position_id: null,
      compensation: [],
    };
    
    setEditedWorkExperiences([newWorkExperience, ...editedWorkExperiences]);
    
    // Track work experience addition
    trackFeatureUsage('work_experience_add', 'initiated', {
      journey_id: journeyIdRef.current,
      current_experiences_count: editedWorkExperiences.length,
      is_first_experience: editedWorkExperiences.length === 0
    });
  };

  const handleWorkExperienceChange = (
    index: number,
    field: keyof WorkExperience,
    value: string | number | boolean | null | CompensationEntry[]
  ) => {
    setEditedWorkExperiences(prev => {
      const updatedExperiences = [...prev];
      switch (field) {
        case 'is_promotion':
        case 'is_current':
          updatedExperiences[index] = {
            ...updatedExperiences[index],
            [field]: value as boolean,
            ...(field === 'is_promotion' ? {
              previous_position_id: null,
              previous_position: null,
            } : {}),
            ...(field === 'is_current' ? {
              end_date: value ? null : updatedExperiences[index].end_date,
            } : {})
          };
          break;
        case 'compensation':
          updatedExperiences[index].compensation = Array.isArray(value) ? value : [];
          break;
        case 'previous_position_id': {
          const previousPosition = prev.find(exp => exp.id === value);
          updatedExperiences[index] = {
            ...updatedExperiences[index],
            [field]: value === '' ? null : (value as string),
            previous_position: previousPosition || null,
          };
          break;
        }
        default:
          updatedExperiences[index] = {
            ...updatedExperiences[index],
            [field]: value
          };
      }
      return updatedExperiences;
    });
  };

  const handleAddCompensation = (experienceIndex: number) => {
    setEditedWorkExperiences(prev => {
      const updated = [...prev];
      // Initialize the work experience if it doesn't exist
      if (!updated[experienceIndex]) {
        updated[experienceIndex] = {
          company: '',
          position: '',
          location: '',
          start_date: '',
          end_date: null,
          description: '',
          is_promotion: false,
          is_current: false,
          previous_position_id: null,
          compensation: []
        };
      }

      // Ensure compensation is an array
      if (!Array.isArray(updated[experienceIndex].compensation)) {
        updated[experienceIndex].compensation = [];
      }

      // Add new compensation entry only if it doesn't already exist
      const newCompensation: CompensationEntry = {
        type: 'salary' as const,
        amount: 0,
        currency: 'USD',
        effective_date: new Date().toISOString().split('T')[0],
        end_date: null,
      };

      // Check if this exact compensation entry already exists
      const exists = updated[experienceIndex].compensation.some(comp => 
        comp.type === newCompensation.type &&
        comp.amount === newCompensation.amount &&
        comp.currency === newCompensation.currency &&
        comp.effective_date === newCompensation.effective_date
      );

      if (!exists) {
        updated[experienceIndex].compensation.push(newCompensation);
      }

      return updated;
    });
  };

  const handleCompensationChange = (
    experienceIndex: number,
    compensationIndex: number,
    field: keyof CompensationEntry,
    value: string | number | boolean | Partial<CompensationDetails>
  ) => {
    setEditedWorkExperiences(prev => {
      const updated = [...prev];
      const updatedCompensation = [...updated[experienceIndex].compensation];

      if (field === 'amount') {
        // Parse the value and ensure it's a valid number
        const parsedValue = typeof value === 'string' ? parseFloat(value) : value as number;
        updatedCompensation[compensationIndex] = {
          ...updatedCompensation[compensationIndex],
          [field]: isNaN(parsedValue) ? 0 : parsedValue
        };
      } else if (field === 'details') {
        // Handle nested details object with proper typing
        const details = value as Partial<CompensationDetails>;
        const currentDetails = updatedCompensation[compensationIndex].details || {};
        
        updatedCompensation[compensationIndex] = {
          ...updatedCompensation[compensationIndex],
          details: {
            ...currentDetails,
            ...details,
            // Convert string values to numbers where needed
            shares: details.shares !== undefined ? 
              (typeof details.shares === 'string' ? parseInt(details.shares) : details.shares) : 
              currentDetails.shares,
            strikePrice: details.strikePrice !== undefined ? 
              (typeof details.strikePrice === 'string' ? parseFloat(details.strikePrice) : details.strikePrice) : 
              currentDetails.strikePrice
          }
        };
      } else {
        // For other fields, just update normally with proper typing
        updatedCompensation[compensationIndex] = {
          ...updatedCompensation[compensationIndex],
          [field]: value
        };
      }

      updated[experienceIndex].compensation = updatedCompensation;
      return updated;
    });
  };

  const handleRemoveCompensation = (experienceIndex: number, compensationIndex: number) => {
    setEditedWorkExperiences(prev => {
      const updated = [...prev];
      updated[experienceIndex].compensation.splice(compensationIndex, 1);
      return updated;
    });
  };

  const toggleCompensationHistory = (experienceId: string) => {
    setExpandedCompensation(prev => ({
      ...prev,
      [experienceId]: !prev[experienceId]
    }));
  };

  const hasCompensationHistory = (experience: WorkExperience) => {
    return experience.compensation && experience.compensation.length > 0;
  };

  const hasLinkedContent = (experienceId: string) => {
    // If we're loading, show as loading state
    if (isLoadingLinkedContent[experienceId]) {
      return true;
    }
    // If we haven't loaded yet, return false
    if (linkedContent[experienceId] === undefined) {
      return false;
    }
    // Check if we have any linked content (even if it's just placeholder data)
    return linkedContent[experienceId]?.length > 0;
  };

  const hasDocuments = (experienceId: string) => {
    // If we're loading, show as loading state
    if (isLoadingDocuments[experienceId]) {
      return true;
    }
    // If we haven't loaded yet, return false
    if (workExperienceDocuments[experienceId] === undefined) {
      return false;
    }
    return workExperienceDocuments[experienceId]?.length > 0;
  };

  const toggleLinkedContent = async (experienceId: string) => {
    // Toggle the expanded state
    setExpandedLinkedContent(prev => ({
      ...prev,
      [experienceId]: !prev[experienceId]
    }));

    // Fetch linked content if not already loaded
    if (!linkedContent[experienceId] && !isLoadingLinkedContent[experienceId]) {
      await fetchLinkedContent(experienceId);
    }
  };

  const fetchLinkedContent = async (experienceId: string) => {
    // Return early if detailed content is already loaded
    // (We check for preview property to distinguish between detailed content and placeholder)
    if (linkedContent[experienceId]?.[0]?.preview || isLoadingLinkedContent[experienceId]) {
      return;
    }
    
    setIsLoadingLinkedContent(prev => ({ ...prev, [experienceId]: true }));
    try {
      const { data: links, error } = await supabase
        .from('content_links')
        .select('*')
        .eq('target_id', experienceId)
        .eq('target_type', 'work_experience');

      if (error) throw error;

      if (links && links.length > 0) {
        const contentPromises = [];
        const reviewIds = links.filter(link => link.source_type === 'review').map(link => link.source_id);
        const noteIds = links.filter(link => link.source_type === 'note').map(link => link.source_id);
        const cardIds = links.filter(link => link.source_type === 'career_card').map(link => link.source_id);

        // Fetch reviews
        if (reviewIds.length > 0) {
          contentPromises.push(
            supabase
              .from('reviews')
              .select('*')
              .in('id', reviewIds)
              .then(({ data, error }) => {
                if (error) throw error;
                return data?.map(review => ({
                  id: review.id,
                  type: 'review' as const,
                  preview: {
                    title: review.review_name,
                    date: review.review_date,
                    preview: review.review_text.substring(0, 200) + (review.review_text.length > 200 ? '...' : '')
                  }
                })) || [];
              })
          );
        }

        // Fetch notes
        if (noteIds.length > 0) {
          contentPromises.push(
            supabase
              .from('notes')
              .select('*')
              .in('id', noteIds)
              .then(({ data, error }) => {
                if (error) throw error;
                return data?.map(note => ({
                  id: note.id,
                  type: 'note' as const,
                  preview: {
                    title: note.title || 'Untitled Note',
                    date: note.created_at,
                    preview: note.content?.substring(0, 200) + (note.content?.length > 200 ? '...' : '') || 'No content'
                  }
                })) || [];
              })
          );
        }

        // Fetch career cards
        if (cardIds.length > 0) {
          contentPromises.push(
            supabase
              .from('career_cards')
              .select('*')
              .in('cardid', cardIds)
              .then(({ data, error }) => {
                if (error) throw error;
                return data?.map(card => ({
                  id: card.cardid,
                  type: 'career_card' as const,
                  preview: {
                    title: card.card_data.title || 'Untitled Card',
                    date: card.created_at,
                    preview: card.card_data.overview || 'No overview available'
                  }
                })) || [];
              })
          );
        }

        // Wait for all content to be fetched
        const allContent = await Promise.all(contentPromises);
        const flattenedContent = allContent.flat();

        setLinkedContent(prev => ({
          ...prev,
          [experienceId]: flattenedContent
        }));
      } else {
        setLinkedContent(prev => ({
          ...prev,
          [experienceId]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching linked content:', error);
    } finally {
      setIsLoadingLinkedContent(prev => ({ ...prev, [experienceId]: false }));
    }
  };

  const handleUnlinkContent = async (experienceId: string, contentId: string) => {
    try {
      const { error } = await supabase
        .from('content_links')
        .delete()
        .match({ target_id: experienceId, source_id: contentId });

      if (error) throw error;

      // Update local state
      setLinkedContent(prev => ({
        ...prev,
        [experienceId]: prev[experienceId]?.filter(content => content.id !== contentId) || []
      }));

      showToast('Successfully unlinked content', 'success');
    } catch (error) {
      console.error('Error unlinking content:', error);
      showToast('Failed to unlink content', 'error');
    }
  };

  // New handlers for work experience documents
  const fetchWorkExperienceDocuments = async (experienceId: string) => {
    try {
      setIsLoadingDocuments(prev => ({
        ...prev,
        [experienceId]: true
      }));

      const { data: files, error } = await supabase
        .from('files')
        .select('*')
        .eq('usage->>type', 'work_experience_document')
        .eq('usage->>work_experience_id', experienceId)
        .is('deleted_at', null);

      if (error) throw error;

      const documents: WorkExperienceDocument[] = files.map(file => ({
        id: file.id,
        type: 'work_experience_document',
        file_path: file.file_path,
        work_experience_id: file.usage.work_experience_id,
        preview: {
          title: file.metadata?.display_label || file.file_name,
          filename: file.file_name,
          date: file.created_at,
          mime_type: file.mime_type,
          size: file.size,
          source: file.source
        }
      }));

      setWorkExperienceDocuments(prev => ({
        ...prev,
        [experienceId]: documents
      }));
    } catch (error) {
      logger.error('Error fetching work experience documents:', error);
      showToast('Error loading documents', 'error');
    } finally {
      setIsLoadingDocuments(prev => ({
        ...prev,
        [experienceId]: false
      }));
    }
  };

  const handleUploadDocument = async (file: File, displayLabel: string) => {
    const startTime = performance.now();
    
    if (!selectedExperienceId) {
      showToast('No work experience selected', 'error');
      return;
    }

    try {
      await FileService.uploadWorkExperienceDocument({
        file,
        workExperienceId: selectedExperienceId,
        displayLabel
      });
      
      showToast('Document uploaded successfully', 'success');
      await fetchWorkExperienceDocuments(selectedExperienceId);
      
      // Track document upload
      trackFeatureUsage('work_experience_document_upload', 'completed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        file_type: file.type,
        file_size: file.size,
        work_experience_id: selectedExperienceId,
        display_label: displayLabel
      });
    } catch (error) {
      logger.error('Error uploading document:', error);
      showToast('Error uploading document', 'error');
      
      // Track failed document upload
      trackFeatureUsage('work_experience_document_upload', 'failed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        file_type: file.type,
        file_size: file.size,
        work_experience_id: selectedExperienceId
      });
    } finally {
      setShowUploadModal(false);
    }
  };

  const handleViewDocument = async (document: WorkExperienceDocument) => {
    try {
      const url = await FileService.getFileUrl({
        id: document.id,
        file_name: document.preview.filename,
        mime_type: document.preview.mime_type,
        size: document.preview.size,
        source: document.preview.source,
        file_path: document.file_path,
        usage: {
          type: 'work_experience_document',
          work_experience_id: document.work_experience_id
        }
      });
      window.open(url, '_blank');
    } catch (error) {
      logger.error('Error viewing document:', error);
      showToast('Error opening document', 'error');
    }
  };

  const handleDownloadDocument = async (document: WorkExperienceDocument) => {
    try {
      const url = await FileService.getDownloadUrl({
        id: document.id,
        file_name: document.preview.filename,
        mime_type: document.preview.mime_type,
        size: document.preview.size,
        source: document.preview.source,
        file_path: document.file_path,
        usage: {
          type: 'work_experience_document',
          work_experience_id: document.work_experience_id
        }
      });
      
      const downloadLink = window.document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = document.preview.filename;
      window.document.body.appendChild(downloadLink);
      downloadLink.click();
      window.document.body.removeChild(downloadLink);
    } catch (error) {
      logger.error('Error downloading document:', error);
      showToast('Error downloading document', 'error');
    }
  };

  const handleEditDocumentLabel = async (document: WorkExperienceDocument, newLabel: string) => {
    try {
      await FileService.updateDisplayLabel(document.id, newLabel);
      
      // Update local state
      setWorkExperienceDocuments(prev => {
        const experienceId = Object.keys(prev).find(key => 
          prev[key].some(doc => doc.id === document.id)
        );
        
        if (!experienceId) return prev;

        return {
          ...prev,
          [experienceId]: prev[experienceId].map(doc => 
            doc.id === document.id 
              ? { ...doc, preview: { ...doc.preview, title: newLabel } }
              : doc
          )
        };
      });

      showToast('Document label updated', 'success');
    } catch (error) {
      logger.error('Error updating document label:', error);
      showToast('Failed to update document label', 'error');
    }
  };

  const handleDeleteDocument = async (document: WorkExperienceDocument) => {
    try {
      await FileService.deleteFile({
        id: document.id,
        file_name: document.preview.filename,
        mime_type: document.preview.mime_type,
        size: document.preview.size,
        source: document.preview.source,
        file_path: document.file_path,
        usage: {
          type: 'work_experience_document',
          work_experience_id: document.work_experience_id
        }
      });

      // Update local state
      setWorkExperienceDocuments(prev => {
        const experienceId = Object.keys(prev).find(key => 
          prev[key].some(doc => doc.id === document.id)
        );
        
        if (!experienceId) return prev;

        return {
          ...prev,
          [experienceId]: prev[experienceId].filter(doc => doc.id !== document.id)
        };
      });

      showToast('Document deleted successfully', 'success');
    } catch (error) {
      logger.error('Error deleting document:', error);
      showToast('Failed to delete document', 'error');
    }
  };

  const formatCompensationType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'salary': 'Salary',
      'hourly': 'Hourly',
      'bonus': 'Bonus',
      'stock_options': 'Stock Options',
      'restricted_stock_units': 'RSUs',
      'other': 'Other'
    };
    return typeMap[type] || type;
  };

  const formatPayFrequency = (frequency: string): string => {
    const frequencyMap: { [key: string]: string } = {
      'yearly': 'Yearly',
      'monthly': 'Monthly',
      'bi-weekly': 'Bi-Weekly',
      'weekly': 'Weekly',
      'hourly': 'Hourly',
      'one_time': 'One Time'
    };
    return frequencyMap[frequency] || frequency;
  };

  const renderCompensationFields = (experience: WorkExperience, experienceIndex: number) => (
    <div className="mt-4 p-4 border rounded">
      <h4 className="font-semibold mb-2">Compensation History</h4>
      {Array.isArray(experience.compensation) && experience.compensation.map((comp, compIndex) => (
        <div key={compIndex} className="mb-4 p-2 border rounded">
          <div className="grid grid-cols-2 gap-4">
            <select
              value={comp.type}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'type', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
              title="Compensation Type"
              aria-label="Select compensation type"
            >
              <option value="salary">Salary</option>
              <option value="hourly">Hourly</option>
              <option value="bonus">Bonus</option>
              <option value="stock_options">Stock Options</option>
              <option value="restricted_stock_units">RSUs</option>
              <option value="other">Other</option>
            </select>
            <input
              type="number"
              placeholder="Amount"
              value={comp.amount || 0}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'amount', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
            />
            <input
              type="text"
              placeholder="Currency"
              value={comp.currency}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'currency', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
            />
            <select
              value={comp.pay_frequency || ''}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'pay_frequency', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
              title="Pay Frequency"
              aria-label="Select pay frequency"
            >
              <option value="">Select Pay Frequency</option>
              <option value="yearly">Yearly</option>
              <option value="monthly">Monthly</option>
              <option value="bi-weekly">Bi-Weekly</option>
              <option value="weekly">Weekly</option>
              <option value="hourly">Hourly</option>
              <option value="one_time">One Time</option>
            </select>
            <input
              type="date"
              placeholder="Effective Date"
              value={comp.effective_date}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'effective_date', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
            />
            <input
              type="date"
              placeholder="End Date (if applicable)"
              value={comp.end_date || ''}
              onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'end_date', e.target.value)}
              className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
            />
          </div>
          {(comp.type === 'stock_options' || comp.type === 'restricted_stock_units') && (
            <div className="mt-2 grid grid-cols-2 gap-4">
              <input
                type="number"
                placeholder="Number of Shares"
                value={comp.details?.shares ?? ''}
                onChange={(e) => handleCompensationChange(
                  experienceIndex, 
                  compIndex, 
                  'details', 
                  { 
                    ...comp.details, 
                    shares: e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                  }
                )}
                className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
              />
              <input
                type="text"
                placeholder="Vesting Schedule"
                value={comp.details?.vestingSchedule || ''}
                onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'details', { ...comp.details, vestingSchedule: e.target.value })}
                className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
              />
              {comp.type === 'stock_options' && (
                <input
                  type="number"
                  placeholder="Strike Price"
                  value={comp.details?.strikePrice ?? ''}
                  onChange={(e) => handleCompensationChange(
                    experienceIndex, 
                    compIndex, 
                    'details', 
                    { 
                      ...comp.details, 
                      strikePrice: e.target.value === '' ? undefined : parseFloat(e.target.value)
                    }
                  )}
                  className={`p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                />
              )}
            </div>
          )}
          <textarea
            placeholder="Additional Details"
            value={comp.details?.additionalInfo || ''}
            onChange={(e) => handleCompensationChange(experienceIndex, compIndex, 'details', { ...comp.details, additionalInfo: e.target.value })}
            className={`mt-2 p-2 border rounded w-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
            rows={3}
          />
          <button
            onClick={() => handleRemoveCompensation(experienceIndex, compIndex)}
            className="mt-2 bg-red-500 text-white px-2 py-1 rounded"
            title="Remove compensation entry"
            aria-label="Remove compensation entry"
          >
            Remove Compensation
          </button>
        </div>
      ))}
      <button
        onClick={() => handleAddCompensation(experienceIndex)}
        className="bg-green-500 text-white px-2 py-1 rounded"
        title="Add new compensation entry"
        aria-label="Add new compensation entry"
      >
        Add Compensation
      </button>
    </div>
  );

  const handleRemoveWorkExperience = (index: number) => {
    setEditedWorkExperiences(editedWorkExperiences.filter((_, i) => i !== index));
  };

  // Add type guard function at the top level
  const isValidExperience = (experience: WorkExperience): experience is WorkExperience & { id: string } => {
    return typeof experience.id === 'string' && experience.id.length > 0;
  };

  // Add this type guard function near the other utility functions
  const hasValidId = (experience: WorkExperience): experience is WorkExperience & { id: string } => {
    return typeof experience.id === 'string' && experience.id.length > 0;
  };

  const getDocumentButtonClass = (experience: WorkExperience) => {
    if (!hasValidId(experience)) return 'text-gray-400 cursor-not-allowed';
    if (isLoadingDocuments[experience.id]) return 'text-blue-500 hover:text-blue-700';
    if (hasDocuments(experience.id)) return 'text-blue-500 hover:text-blue-700';
    return 'text-gray-400 cursor-not-allowed';
  };

  if (!user) {
    return <div className="flex justify-center items-center h-full">Loading...</div>;
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Profile"
        primaryButton={
          isEditing ? (
            <div className="flex space-x-2">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          ) : (
            <button
              onClick={handleEditProfile}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Edit Profile
            </button>
          )
        }
      />
      <div className="flex-1 overflow-y-auto" style={{ paddingTop: `${PAGE_HEADER_HEIGHT}px` }}>
        <div className="p-4">
          {profile && !profile.onboarding_complete && (
            <OnboardingBanner className="mb-4" />
          )}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md rounded-lg p-6`}>
            {/* Profile fields */}
            {(['first_name', 'last_name', 'location', 'professional_summary', 'career_objectives'] as const).map((field) => (
              <div key={field} className="mb-4">
                <label className="block text-sm font-bold mb-2" htmlFor={field}>
                  {field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </label>
                {isEditing ? (
                  field === 'professional_summary' || field === 'career_objectives' ? (
                    <textarea
                      className={`shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                      id={field}
                      name={field}
                      value={editedProfile?.[field] || ''}
                      onChange={handleChange}
                      rows={4}
                    />
                  ) : (
                    <input
                      className={`shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                      id={field}
                      type="text"
                      name={field}
                      value={editedProfile?.[field] || ''}
                      onChange={handleChange}
                    />
                  )
                ) : (
                  <p>{profile?.[field] || 'Not set'}</p>
                )}
              </div>
            ))}

            {/* Skills */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2" htmlFor="skills">
                Skills
              </label>
              {isEditing ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {editedProfile?.skills?.map((skill, index) => (
                      <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded flex items-center">
                        {skill}
                        <button
                          onClick={() => handleRemoveSkill(skill)}
                          className="ml-1 text-blue-800 font-bold"
                          title="Remove skill"
                          aria-label={`Remove ${skill} skill`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={handleSkillInputKeyDown}
                      placeholder="Add a new skill"
                      className={`grow p-2 border rounded-l-md ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                    />
                    <button
                      onClick={handleAddSkill}
                      className="bg-blue-500 text-white px-4 rounded-r-md"
                    >
                      Add
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profile?.skills?.map((skill, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      {skill}
                    </span>
                  )) || 'No skills added'}
                </div>
              )}
            </div>

            {/* Email Addresses */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Email Addresses</h2>
                  <button
                    onClick={() => setShowEmailInfoModal(true)}
                    className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                    title="How email notes work"
                  >
                    <span className="text-xs text-gray-600 dark:text-gray-400">?</span>
                  </button>
                </div>
                {/* Show email count (excluding primary) */}
                {emails.length > 0 && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {emails.filter(e => !e.is_primary).length} of 5 Additional Emails used
                  </span>
                )}
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Add multiple email addresses to send notes from different accounts (work, personal, etc.)
                </p>
                <div className="flex items-start gap-2 mt-1">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p>Send emails to <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">notes@careercard.ai</span> to create notes automatically.</p>
                    <p className="mt-1">Each email address can create up to 5 notes per day.</p>
                  </div>
                </div>
              </div>
              
              <EmailList
                emails={emails}
                onRemove={async (emailId) => {
                  try {
                    await removeEmail(emailId);
                    showToast('Email removed successfully', 'success');
                  } catch (error) {
                    showToast('Failed to remove email', 'error');
                  }
                }}
                onEdit={(emailId) => {
                  const email = emails.find(e => e.id === emailId);
                  if (email) {
                    setEditingEmail(email);
                  }
                }}
                onResend={async (emailId) => {
                  try {
                    await resendVerification(emailId);
                    showToast('Verification email sent', 'success');
                  } catch (error) {
                    showToast('Failed to send verification email', 'error');
                  }
                }}
                isLoading={isLoadingEmails}
                workExperiences={workExperiences.filter(we => we.id).map(we => ({ 
                  id: we.id!, 
                  company: we.company, 
                  position: we.position 
                }))}
              />
              
              <div className="mt-4">
                <AddEmailForm
                  onAdd={async (email) => {
                    const result = await addEmail(email);
                    if (result?.message) {
                      showToast(result.message, 'success');
                    }
                  }}
                  workExperiences={workExperiences.filter(we => we.id).map(we => ({ 
                  id: we.id!, 
                  company: we.company, 
                  position: we.position 
                }))}
                  isLoading={isLoadingEmails}
                  canAddMore={emails.filter(e => !e.is_primary).length < 5}
                />
              </div>
            </div>

            {/* Work Experience */}
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">Work Experience</h2>
              {isEditing ? (
                <div>
                  <div className="mb-4">
                    <button
                      onClick={handleAddWorkExperience}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded flex items-center mb-4"
                    >
                      <IoAddOutline className="mr-2" />
                      Add Work Experience
                    </button>
                    {editedWorkExperiences.length === 0 && (
                      <p className="text-gray-500 italic">Click to add your first work experience.</p>
                    )}
                  </div>
                  {editedWorkExperiences.map((experience, index) => (
                    <div key={index} className="mb-4 p-4 border rounded">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">Work Experience #{index + 1}</h3>
                        <button
                          onClick={() => handleRemoveWorkExperience(index)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove work experience"
                          aria-label="Remove work experience"
                        >
                          <IoTrashOutline size={20} />
                        </button>
                      </div>
                      <input
                        className={`shadow appearance-none border rounded w-full py-2 px-3 mb-2 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                        type="text"
                        placeholder="Company"
                        value={experience.company}
                        onChange={(e) => handleWorkExperienceChange(index, 'company', e.target.value)}
                      />
                      <input
                        className={`shadow appearance-none border rounded w-full py-2 px-3 mb-2 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                        type="text"
                        placeholder="Position"
                        value={experience.position}
                        onChange={(e) => handleWorkExperienceChange(index, 'position', e.target.value)}
                      />
                      <input
                        className={`shadow appearance-none border rounded w-full py-2 px-3 mb-2 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                        type="text"
                        placeholder="Location"
                        value={experience.location}
                        onChange={(e) => handleWorkExperienceChange(index, 'location', e.target.value)}
                      />
                      <div className="flex mb-2">
                        <div className="w-1/2 mr-2">
                          <label className="block text-sm font-bold mb-2" htmlFor={`start_date_${index}`}>
                            Start Date
                          </label>
                          <input
                            id={`start_date_${index}`}
                            className={`shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                            type="date"
                            value={experience.start_date}
                            onChange={(e) => handleWorkExperienceChange(index, 'start_date', e.target.value)}
                          />
                        </div>
                        {!experience.is_current && (
                          <div className="w-1/2">
                            <label className="block text-sm font-bold mb-2" htmlFor={`end_date_${index}`}>
                              End Date
                            </label>
                            <input
                              id={`end_date_${index}`}
                              className={`shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                              type="date"
                              value={experience.end_date || ''}
                              onChange={(e) => handleWorkExperienceChange(index, 'end_date', e.target.value || null)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="mt-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={experience.is_current}
                            onChange={(e) => handleWorkExperienceChange(index, 'is_current', e.target.checked)}
                            className="mr-2"
                          />
                          This is my current role
                        </label>
                      </div>
                      <textarea
                        className={`shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                        placeholder="Description"
                        value={experience.description}
                        onChange={(e) => handleWorkExperienceChange(index, 'description', e.target.value)}
                        rows={4}
                      />
                      <div className="mt-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={experience.is_promotion}
                            onChange={(e) => handleWorkExperienceChange(index, 'is_promotion', e.target.checked)}
                            className="mr-2"
                          />
                          This is a promotion
                        </label>
                      </div>
                      {experience.is_promotion && (
                        <select
                          className={`mt-2 w-full p-2 border rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}
                          value={experience.previous_position_id || ''}
                          onChange={(e) => handleWorkExperienceChange(index, 'previous_position_id', e.target.value)}
                          title="Previous Position"
                          aria-label="Select previous position"
                        >
                          <option key="default" value="">Select previous position</option>
                          {editedWorkExperiences
                            .filter(exp => exp.id !== experience.id)
                            .map((prev) => (
                              <option key={prev.id || `temp-${prev.company}-${prev.position}`} value={prev.id || ''}>
                                {prev.position} at {prev.company}
                              </option>
                            ))}
                        </select>
                      )}
                      <div className="mt-4">
                        {renderCompensationFields(experience, index)}
                      </div>

                      {/* Linked Content Section */}
                      <div className="mt-4">
                        <button
                          onClick={() => {
                            if (hasValidId(experience)) {
                              toggleLinkedContent(experience.id);
                            }
                          }}
                          className={`flex items-center ${
                            hasValidId(experience) && (
                              isLoadingLinkedContent[experience.id]
                                ? 'text-blue-500 hover:text-blue-700'
                                : hasLinkedContent(experience.id)
                                  ? 'text-blue-500 hover:text-blue-700'
                                  : 'text-gray-400'
                            )
                          } mb-2`}
                          disabled={!hasValidId(experience)}
                        >
                          <span className="mr-2">Linked Content</span>
                          {hasValidId(experience) && expandedLinkedContent[experience.id] ? <IoChevronUp /> : <IoChevronDown />}
                        </button>
                        {hasValidId(experience) && expandedLinkedContent[experience.id] && (
                          <div className="mt-2 pl-4 border-l-2 border-gray-300">
                            {isLoadingLinkedContent[experience.id] ? (
                              <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                              </div>
                            ) : linkedContent[experience.id]?.length > 0 ? (
                              linkedContent[experience.id].map((content: LinkedContent) => (
                                <LinkedContentPreview
                                  key={content.id}
                                  content={{
                                    ...content,
                                    type: content.type
                                  }}
                                  onUnlink={isEditing ? () => handleUnlinkContent(experience.id, content.id) : undefined}
                                />
                              ))
                            ) : (
                              <p className="text-gray-500 dark:text-gray-400 italic">No linked content yet.</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Documents Section */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={() => {
                              if (hasValidId(experience)) {
                                if (!workExperienceDocuments[experience.id]) {
                                  fetchWorkExperienceDocuments(experience.id);
                                }
                                setExpandedDocuments(prev => ({
                                  ...prev,
                                  [experience.id]: !prev[experience.id]
                                }));
                              }
                            }}
                            className={`flex items-center ${getDocumentButtonClass(experience)}`}
                            disabled={!hasValidId(experience)}
                          >
                            <IoDocumentOutline className="mr-2" />
                            <span className="mr-2">Documents</span>
                            {hasValidId(experience) && expandedDocuments[experience.id] ? <IoChevronUp /> : <IoChevronDown />}
                          </button>
                            {isEditing && (
                              <button
                                onClick={() => {
                                  if (!isValidExperience(experience)) {
                                    showToast('Invalid work experience', 'error');
                                    return;
                                  }
                                  setSelectedExperienceId(experience.id);
                                  setShowUploadModal(true);
                                }}
                                className="flex items-center text-blue-500 hover:text-blue-700"
                                title="Add new document"
                                aria-label="Add new document"
                                disabled={!isValidExperience(experience)}
                              >
                                <IoAddOutline className="mr-1" />
                                Add Document
                              </button>
                            )}
                        </div>
                        {hasValidId(experience) && expandedDocuments[experience.id] && (
                          <div className="mt-2 pl-4 border-l-2 border-gray-300">
                            {isLoadingDocuments[experience.id] ? (
                              <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                              </div>
                            ) : workExperienceDocuments[experience.id]?.length > 0 ? (
                              <div className="space-y-2">
                                {workExperienceDocuments[experience.id].map((document) => (
                                  <WorkExperienceDocumentPreview
                                    key={document.id}
                                    content={document}
                                    onView={() => handleViewDocument(document)}
                                    onDownload={() => handleDownloadDocument(document)}
                                    onEditLabel={isEditing ? 
                                      (newLabel: string) => handleEditDocumentLabel(document, newLabel) 
                                      : undefined}
                                    onDelete={isEditing ? 
                                      () => handleDeleteDocument(document) 
                                      : undefined}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 dark:text-gray-400 italic">
                                No documents attached yet.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {workExperiences.map((experience, index) => {
                    const experienceId = hasValidId(experience) ? experience.id : null;
                    return (
                      <div 
                        key={index} 
                        id={experienceId ? `experience-${experienceId}` : undefined}
                        className="mb-4 p-4 border rounded transition-all duration-300"
                      >
                        <h3 className="text-lg font-semibold">{experience.position} at {experience.company}</h3>
                        <p className="text-sm text-gray-600">
                          {formatDateOnlyWithTimezone(experience.start_date)} - {experience.is_current ? 'Present' : (experience.end_date ? formatDateOnlyWithTimezone(experience.end_date) : 'Present')}
                        </p>
                        <p className="mt-2">{experience.description}</p>
                        {experience.is_promotion && experience.previous_position && (
                          <p className="mt-2 text-sm text-gray-600">
                            Promotion from: {experience.previous_position.position} at {experience.previous_position.company}
                          </p>
                        )}
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              if (hasCompensationHistory(experience) && hasValidId(experience)) {
                                toggleCompensationHistory(experience.id);
                              }
                            }}
                            className={`flex items-center ${
                              hasCompensationHistory(experience)
                                ? 'text-blue-500 hover:text-blue-700'
                                : 'text-gray-400 cursor-not-allowed'
                            } mb-2`}
                            disabled={!hasCompensationHistory(experience) || !hasValidId(experience)}
                          >
                            <span className="mr-2">Compensation History</span>
                            {hasCompensationHistory(experience) && hasValidId(experience) && (
                              expandedCompensation[experience.id] ? <IoChevronUp /> : <IoChevronDown />
                            )}
                          </button>
                          {hasValidId(experience) && expandedCompensation[experience.id] && hasCompensationHistory(experience) && (
                            <div className="mt-2 pl-4 border-l-2 border-gray-300">
                              {experience.compensation.map((comp, compIndex) => (
                                <div key={compIndex} className="mt-2 pl-4 border-l-2 border-gray-300">
                                  <p>Type: {formatCompensationType(comp.type)}</p>
                                  <p>Amount: {comp.amount} {comp.currency}</p>
                                  {comp.pay_frequency && <p>Pay Frequency: {formatPayFrequency(comp.pay_frequency)}</p>}
                                  <p>Effective: {formatDateOnlyWithTimezone(comp.effective_date)}</p>
                                  {comp.end_date && <p>End: {formatDateOnlyWithTimezone(comp.end_date)}</p>}
                                  {comp.details && (
                                    <div>
                                      {comp.details.shares && <p>Shares: {comp.details.shares}</p>}
                                      {comp.details.vestingSchedule && <p>Vesting: {comp.details.vestingSchedule}</p>}
                                      {comp.details.strikePrice && <p>Strike Price: {comp.details.strikePrice}</p>}
                                      {comp.details.additionalInfo && <p>Additional Info: {comp.details.additionalInfo}</p>}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Linked Content Section */}
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              if (hasValidId(experience)) {
                                toggleLinkedContent(experience.id);
                              }
                            }}
                            className={`flex items-center ${
                              hasValidId(experience) && (
                                isLoadingLinkedContent[experience.id]
                                  ? 'text-blue-500 hover:text-blue-700'
                                  : hasLinkedContent(experience.id)
                                    ? 'text-blue-500 hover:text-blue-700'
                                    : 'text-gray-400'
                              )
                            } mb-2`}
                            disabled={!hasValidId(experience)}
                          >
                            <span className="mr-2">Linked Content</span>
                            {hasValidId(experience) && expandedLinkedContent[experience.id] ? <IoChevronUp /> : <IoChevronDown />}
                          </button>
                          {hasValidId(experience) && expandedLinkedContent[experience.id] && (
                            <div className="mt-2 pl-4 border-l-2 border-gray-300">
                              {isLoadingLinkedContent[experience.id] ? (
                                <div className="animate-pulse">
                                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                </div>
                              ) : linkedContent[experience.id]?.length > 0 ? (
                                linkedContent[experience.id].map((content: LinkedContent) => (
                                  <LinkedContentPreview
                                    key={content.id}
                                    content={{
                                      ...content,
                                      type: content.type
                                    }}
                                    onUnlink={isEditing ? () => handleUnlinkContent(experience.id, content.id) : undefined}
                                  />
                                ))
                              ) : (
                                <p className="text-gray-500 dark:text-gray-400 italic">No linked content yet.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Documents Section */}
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <button
                              onClick={() => {
                                if (hasValidId(experience)) {
                                  if (!workExperienceDocuments[experience.id]) {
                                    fetchWorkExperienceDocuments(experience.id);
                                  }
                                  setExpandedDocuments(prev => ({
                                    ...prev,
                                    [experience.id]: !prev[experience.id]
                                  }));
                                }
                              }}
                              className={`flex items-center ${getDocumentButtonClass(experience)}`}
                              disabled={!hasValidId(experience)}
                            >
                              <IoDocumentOutline className="mr-2" />
                              <span className="mr-2">Documents</span>
                              {hasValidId(experience) && expandedDocuments[experience.id] ? <IoChevronUp /> : <IoChevronDown />}
                            </button>
                            {isEditing && (
                              <button
                                onClick={() => {
                                  if (!isValidExperience(experience)) {
                                    showToast('Invalid work experience', 'error');
                                    return;
                                  }
                                  setSelectedExperienceId(experience.id);
                                  setShowUploadModal(true);
                                }}
                                className="flex items-center text-blue-500 hover:text-blue-700"
                                title="Add new document"
                                aria-label="Add new document"
                                disabled={!isValidExperience(experience)}
                              >
                                <IoAddOutline className="mr-1" />
                                Add Document
                              </button>
                            )}
                          </div>
                          {hasValidId(experience) && expandedDocuments[experience.id] && (
                            <div className="mt-2 pl-4 border-l-2 border-gray-300">
                              {isLoadingDocuments[experience.id] ? (
                                <div className="animate-pulse">
                                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                </div>
                              ) : workExperienceDocuments[experience.id]?.length > 0 ? (
                                <div className="space-y-2">
                                  {workExperienceDocuments[experience.id].map((document) => (
                                    <WorkExperienceDocumentPreview
                                      key={document.id}
                                      content={document}
                                      onView={() => handleViewDocument(document)}
                                      onDownload={() => handleDownloadDocument(document)}
                                      onEditLabel={isEditing ? 
                                        (newLabel: string) => handleEditDocumentLabel(document, newLabel) 
                                        : undefined}
                                      onDelete={isEditing ? 
                                        () => handleDeleteDocument(document) 
                                        : undefined}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <p className="text-gray-500 dark:text-gray-400 italic">
                                  No documents attached yet.
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add the upload modal */}
            <WorkExperienceDocumentUploadModal
              isOpen={showUploadModal}
              onClose={() => {
                setShowUploadModal(false);
                setSelectedExperienceId(null);
              }}
              onUpload={handleUploadDocument}
            />

            <div className="mt-8">
              <label className="block text-sm font-bold mb-2">Joined</label>
              <p>{profile?.created_at ? formatDateOnlyWithTimezone(profile.created_at, { month: 'long', year: 'numeric' }) : 'Not available'}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Email Edit Modal */}
      <EditEmailModal
        isOpen={!!editingEmail}
        onClose={() => setEditingEmail(null)}
        email={editingEmail}
        workExperiences={workExperiences.filter(we => we.id).map(we => ({ 
          id: we.id!, 
          company: we.company, 
          position: we.position 
        }))}
        onSave={async (emailId, updates) => {
          await updateEmail({
            emailId,
            label: updates.label,
            work_experience_id: updates.work_experience_id
          });
          showToast('Email settings updated', 'success');
          setEditingEmail(null);
        }}
      />
      
      {/* Email Info Modal */}
      <EmailInfoModal
        isOpen={showEmailInfoModal}
        onClose={() => setShowEmailInfoModal(false)}
      />
    </div>
  );
};

export default Profile;